import { computed, nextTick, ref } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { 
  introSteps, 
  creationSteps, 
  postCreationSteps, 
  welcomeStep, 
  portfolioTourSteps,
  clickAddForSecondStep,
  clickAddForSellStep,
  clickAddForCustomStep,
  firstTransactionSteps,
  secondTransactionSteps,
  sellTransactionSteps,
  customSymbolSearchSteps,
  customSymbolCreationSteps,
  customSymbolFormSteps,
  useCustomSymbolSteps,
  customSymbolTransactionSteps,
  analysisButtonStep,
  analysisContentSteps,
  netWorthTourSteps,
  liabilitiesTourSteps
} from '@/config/onboardingTour';
import { TOUR_KEYS, isTourCompleted } from '@/config/tourKeys';
import { authService } from '@/services/authService';

// Shared reactive state outside the function to persist across component instances
const transactionStepRef = ref(0); // 0: initial, 1: first (AAPL), 2: second (XAUUSD), 3: sell, 4: custom search, 5: custom create, 6: use custom
const transactionCountRef = ref(0);
const showSkipModalRef = ref(false); // Controls the skip confirmation modal
let lastPortfolioId = null; // Track which portfolio user came from
let currentDriver = null; // Shared driver instance across all components
let pendingTimeout = null; // Track pending setTimeout to cancel if needed
let isPausingTour = false; // Prevent completion callbacks when pausing
let activeTourSteps = null; // Last steps used for the currently running tour
let activeTourOnComplete = null; // Last onComplete callback for the currently running tour
let pausedTourState = null; // { index: number } when user clicks X
let pendingSymbolSelectResume = null; // { steps, startIndex } when we close step-1 on typing

export function useOnboarding() {
  const store = useStore();
  const router = useRouter();
  const user = computed(() => store.state.auth.user);
  
  // Helper to check if a specific tour is completed
  const checkTourCompleted = (tourKey) => {
    return isTourCompleted(user.value?.onboardingProgress, tourKey);
  };

  // Helper to check if all main onboarding tours are completed
  const isMainOnboardingCompleted = () => {
    return checkTourCompleted(TOUR_KEYS.MAIN);
  };

  // Helper to check if portfolio-related tours are completed
  const isPortfolioOnboardingCompleted = () => {
    return checkTourCompleted(TOUR_KEYS.TRANSACTIONS) && 
           checkTourCompleted(TOUR_KEYS.ANALYSIS);
  };

  // Skip onboarding handler - marks all remaining tours as skipped (internal use)
  const skipOnboarding = async () => {
    destroyCurrentTour();
    
    try {
      // Get all tour keys that aren't completed yet and skip them
      const toursToSkip = Object.values(TOUR_KEYS).filter(
        key => !checkTourCompleted(key)
      );
      
      if (toursToSkip.length > 0) {
        await authService.skipAllTours(toursToSkip);
      }
      
      // Refresh user data to reflect changes
      await store.dispatch('auth/fetchUser');
    } catch (error) {
      console.error('Failed to skip onboarding:', error);
    }
  };

  // Show the skip confirmation modal
  const handleSkipRequest = () => {
    // Pause the tour so the warning modal is fully clickable.
    // We'll resume the tour at the same step if the user cancels.
    try {
      pausedTourState = {
        index: typeof currentDriver?.getActiveIndex === 'function' ? currentDriver.getActiveIndex() : 0
      };
    } catch (e) {
      pausedTourState = { index: 0 };
    }

    // Destroy driver without triggering "onComplete" flows.
    isPausingTour = true;
    destroyCurrentTour();
    isPausingTour = false;

    showSkipModalRef.value = true;
  };

  // Hide the skip confirmation modal (user chose to continue)
  const hideSkipModal = () => {
    showSkipModalRef.value = false;

    // Resume the paused tour (if any)
    if (pausedTourState && activeTourSteps) {
      const startIndex = Math.max(0, pausedTourState.index || 0);
      pausedTourState = null;

      // Ensure DOM is ready before resuming
      nextTick().then(() => {
        destroyCurrentTour();
        currentDriver = createDriver(activeTourOnComplete);
        currentDriver.setSteps(activeTourSteps);
        try {
          currentDriver.drive(startIndex);
        } catch (e) {
          // Fallback if driver version doesn't support indexing
          currentDriver.drive();
        }
      });
    } else {
      pausedTourState = null;
    }
  };

  // User confirmed they want to skip permanently
  const confirmSkipOnboarding = async () => {
    showSkipModalRef.value = false;
    pausedTourState = null;
    await skipOnboarding();
  };

  // Helper to create driver instance
  const createDriver = (onComplete) => driver({
    showProgress: true,
    animate: true,
    allowClose: true, // Allow closing/skipping the tour
    disableActiveInteraction: false, // Allow clicking on highlighted elements
    doneBtnText: 'Done',
    nextBtnText: 'Next',
    prevBtnText: 'Previous',
    closeBtnText: '×',
    popoverClass: 'driverjs-theme',
    onCloseClick: () => {
      // User clicked skip/close button - show confirmation dialogs
      handleSkipRequest();
    },
    onDestroyed: () => {
      currentDriver = null;
      // Only treat destruction as "completed" when it's not a pause
      if (!isPausingTour && onComplete) onComplete();
    }
  });

  const destroyCurrentTour = () => {
    // Cancel any pending timeout first
    if (pendingTimeout) {
      clearTimeout(pendingTimeout);
      pendingTimeout = null;
    }
    // Then destroy the driver if it exists
    if (currentDriver) {
      try {
        currentDriver.destroy();
      } catch (e) {
        console.warn('Error destroying driver:', e);
      }
      currentDriver = null;
    }
  };

  // Workaround for symbol search dropdown visibility:
  // Close step-1 popover as soon as user types, then resume at step-2 after user selects a symbol.
  const onTransactionSymbolSelected = async () => {
    if (!pendingSymbolSelectResume || !pendingSymbolSelectResume.steps) return;

    const { steps, startIndex } = pendingSymbolSelectResume;
    pendingSymbolSelectResume = null;

    await nextTick();

    // Give the UI a moment to apply selected symbol and close dropdown
    setTimeout(() => {
      destroyCurrentTour();
      currentDriver = createDriver(activeTourOnComplete);
      activeTourSteps = steps;
      currentDriver.setSteps(steps);
      try {
        currentDriver.drive(startIndex);
      } catch (e) {
        // Fallback if drive(index) is unsupported
        currentDriver.drive();
      }
    }, 150);
  };

  const startOnboarding = async () => {
    if (!user.value) return;
    
    // Ensure any previous tour is cleaned up
    destroyCurrentTour();

    if (checkTourCompleted(TOUR_KEYS.MAIN)) {
      return;
    }

    await nextTick();

    const steps = [welcomeStep, ...introSteps];
    
    // We don't want an "onDestroyed" callback that marks completion here,
    // because this is just Part 1.
    activeTourOnComplete = () => {
        // Optional: Handle if user skips early
    };
    currentDriver = createDriver(activeTourOnComplete);

    const validSteps = steps.filter(step => {
      if (!step.element) return true; 
      return document.querySelector(step.element);
    });

    if (validSteps.length > 0) {
      activeTourSteps = validSteps;
      currentDriver.setSteps(validSteps);
      currentDriver.drive();
    }
  };

  const startCreationTour = async () => {
    // Only run if onboarding is not complete
    if (checkTourCompleted(TOUR_KEYS.MAIN)) return;

    // Ensure previous driver is gone (should already be destroyed by button click)
    destroyCurrentTour();

    await nextTick();
    
    // Wait for modal to fully render
    pendingTimeout = setTimeout(() => {
        pendingTimeout = null;
        // Mark portfolio creation tour as complete when user finishes/closes this tour
        activeTourOnComplete = async () => {
          try {
            if (!checkTourCompleted(TOUR_KEYS.PORTFOLIO)) {
              await authService.completeTour(TOUR_KEYS.PORTFOLIO);
              // Don't fetch user here - we'll do it in startPostCreationTour
            }
          } catch (error) {
            console.error('Failed to save portfolio creation tour completion', error);
          }
        };
        currentDriver = createDriver(activeTourOnComplete); 
        const validSteps = creationSteps.filter(step => document.querySelector(step.element));
        
        if (validSteps.length > 0) {
            activeTourSteps = validSteps;
            currentDriver.setSteps(validSteps);
            currentDriver.drive();
        }
    }, 500);
  };

  const startPostCreationTour = async () => {
    if (checkTourCompleted(TOUR_KEYS.MAIN)) return;

    destroyCurrentTour();

    await nextTick();

    // Mark MAIN tour as complete immediately since user created their first portfolio
    // This ensures they won't see "create portfolio" prompt again even if they leave
    try {
      await authService.completeTour(TOUR_KEYS.MAIN);
      await store.dispatch('auth/fetchUser');
    } catch (error) {
      console.error('Failed to save main tour completion', error);
    }

    activeTourOnComplete = null;
    currentDriver = createDriver(activeTourOnComplete);

    // Wait for modal to close and list to update
    pendingTimeout = setTimeout(() => {
        pendingTimeout = null;
        const validSteps = postCreationSteps.filter(step => document.querySelector(step.element));
        
        if (validSteps.length > 0) {
            activeTourSteps = validSteps;
            currentDriver.setSteps(validSteps);
            currentDriver.drive();
        } else {
            // If portfolio link not found, maybe they already navigated
            // In that case, we can't guide them further from here
        }
    }, 500);
  };

  const startPortfolioTour = async () => {
    if (!user.value) return;

    destroyCurrentTour();

    if (checkTourCompleted(TOUR_KEYS.TRANSACTIONS)) {
        return;
    }

    // Don't reset if we're already past the initial portfolio tour
    // This prevents wiping progress when returning from custom symbols page
    if (transactionStepRef.value >= 5) {
        return; // Let startUseCustomSymbolTour handle it
    }

    // Only reset if we're truly starting fresh (step 0)
    if (transactionStepRef.value === 0) {
        transactionCountRef.value = 0;
    }

    await nextTick();

    activeTourOnComplete = null;
    currentDriver = createDriver(activeTourOnComplete);

    const validSteps = portfolioTourSteps.filter(step => {
        return document.querySelector(step.element);
    });

    if (validSteps.length > 0) {
        activeTourSteps = validSteps;
        currentDriver.setSteps(validSteps);
        currentDriver.drive();
    }
  };

  const startTransactionModalTour = async (stepType = 'first') => {
    if (checkTourCompleted(TOUR_KEYS.TRANSACTIONS)) return;

    destroyCurrentTour();
    await nextTick();

    let steps = [];
    switch(stepType) {
      case 'first':
        steps = firstTransactionSteps;
        transactionStepRef.value = 1;
        break;
      case 'second':
        steps = secondTransactionSteps;
        transactionStepRef.value = 2;
        break;
      case 'sell':
        steps = sellTransactionSteps;
        transactionStepRef.value = 3;
        break;
      case 'customSearch':
        steps = customSymbolSearchSteps;
        transactionStepRef.value = 4;
        break;
    }

    // Workaround for symbol dropdown visibility (AAPL + Gold + Sell Gold):
    // When user starts typing in the symbol search, close the popover so dropdown is usable.
    // Then after user selects a symbol, resume at the next step index (1).
    if ((stepType === 'first' || stepType === 'second' || stepType === 'sell') && Array.isArray(steps) && steps[0]?.element === '#transaction-symbol-search') {
      const baseSteps = steps;
      steps = baseSteps.map((s, idx) => {
        if (idx !== 0) return s;
        return {
          ...s,
          onHighlightStarted: () => {
            const container = document.querySelector('#transaction-symbol-search');
            const input = container?.querySelector('input');
            if (!input) return;

            const handleInput = () => {
              // Resume at step 2 (index 1) after a symbol is selected from dropdown
              pendingSymbolSelectResume = { steps: baseSteps, startIndex: 1 };

              // Destroy driver so search dropdown can show immediately
              isPausingTour = true;
              destroyCurrentTour();
              isPausingTour = false;
            };

            input.addEventListener('input', handleInput, { once: true });
          }
        };
      });
    }

    pendingTimeout = setTimeout(() => {
      pendingTimeout = null;
      activeTourOnComplete = null;
      currentDriver = createDriver(activeTourOnComplete);
      
      const validSteps = steps.filter(step => {
        if (!step.element) return true;
        return document.querySelector(step.element);
      });
      
      if (validSteps.length > 0) {
        activeTourSteps = validSteps;
        currentDriver.setSteps(validSteps);
        currentDriver.drive();
      }
    }, 500);
  };

  const startCustomSymbolTour = async () => {
    if (checkTourCompleted(TOUR_KEYS.CUSTOM_SYMBOLS)) return;

    destroyCurrentTour();
    await nextTick();

    transactionStepRef.value = 5;

    pendingTimeout = setTimeout(() => {
      pendingTimeout = null;
      activeTourOnComplete = null;
      currentDriver = createDriver(activeTourOnComplete);

      const validSteps = customSymbolCreationSteps.filter(step => document.querySelector(step.element));
      
      if (validSteps.length > 0) {
        activeTourSteps = validSteps;
        currentDriver.setSteps(validSteps);
        currentDriver.drive();
      }
    }, 500);
  };

  const startCustomSymbolFormTour = async () => {
    if (checkTourCompleted(TOUR_KEYS.CUSTOM_SYMBOLS)) return;

    destroyCurrentTour();
    await nextTick();

    pendingTimeout = setTimeout(() => {
      pendingTimeout = null;
      activeTourOnComplete = null;
      currentDriver = createDriver(activeTourOnComplete);

      const validSteps = customSymbolFormSteps.filter(step => document.querySelector(step.element));
      
      if (validSteps.length > 0) {
        activeTourSteps = validSteps;
        currentDriver.setSteps(validSteps);
        currentDriver.drive();
      }
    }, 500);
  };

  const startUseCustomSymbolTour = async () => {
    if (checkTourCompleted(TOUR_KEYS.CUSTOM_SYMBOLS)) return;

    destroyCurrentTour();
    await nextTick();

    transactionStepRef.value = 6; // New step for using custom symbol

    pendingTimeout = setTimeout(() => {
      pendingTimeout = null;
      activeTourOnComplete = null;
      currentDriver = createDriver(activeTourOnComplete);

      const validSteps = useCustomSymbolSteps.filter(step => document.querySelector(step.element));
      
      if (validSteps.length > 0) {
        activeTourSteps = validSteps;
        currentDriver.setSteps(validSteps);
        currentDriver.drive();
      }
    }, 500);
  };

  const startCustomSymbolTransactionTour = async () => {
    if (checkTourCompleted(TOUR_KEYS.CUSTOM_SYMBOLS)) return;

    destroyCurrentTour();
    await nextTick();

    // Workaround for custom symbol search dropdown visibility:
    // close the search step on first typing, resume at step 2 after selection.
    let steps = customSymbolTransactionSteps;
    if (Array.isArray(steps) && steps[0]?.element === '#transaction-symbol-search') {
      const baseSteps = steps;
      steps = baseSteps.map((s, idx) => {
        if (idx !== 0) return s;
        return {
          ...s,
          onHighlightStarted: () => {
            const container = document.querySelector('#transaction-symbol-search');
            const input = container?.querySelector('input');
            if (!input) return;

            const handleInput = () => {
              pendingSymbolSelectResume = { steps: baseSteps, startIndex: 1 };

              isPausingTour = true;
              destroyCurrentTour();
              isPausingTour = false;
            };

            input.addEventListener('input', handleInput, { once: true });
          }
        };
      });
    }

    pendingTimeout = setTimeout(() => {
      pendingTimeout = null;
      activeTourOnComplete = null;
      currentDriver = createDriver(activeTourOnComplete);

      // Filter steps - those without element are always valid (centered popover)
      const validSteps = steps.filter(step => {
        if (!step.element) return true;
        return document.querySelector(step.element);
      });

      if (validSteps.length > 0) {
        activeTourSteps = validSteps;
        currentDriver.setSteps(validSteps);
        currentDriver.drive();
      }
    }, 500);
  };

  const startAnalysisTour = async () => {
    if (checkTourCompleted(TOUR_KEYS.ANALYSIS)) return;

    destroyCurrentTour();
    await nextTick();

    pendingTimeout = setTimeout(() => {
      pendingTimeout = null;
      activeTourOnComplete = null;
      currentDriver = createDriver(activeTourOnComplete);

      const validSteps = analysisButtonStep.filter(step => document.querySelector(step.element));
      
      if (validSteps.length > 0) {
        activeTourSteps = validSteps;
        currentDriver.setSteps(validSteps);
        currentDriver.drive();
      }
    }, 500);
  };

  const startAnalysisContentTour = async () => {
    if (checkTourCompleted(TOUR_KEYS.ANALYSIS)) return;

    destroyCurrentTour();
    await nextTick();

    pendingTimeout = setTimeout(() => {
      pendingTimeout = null;
      activeTourOnComplete = async () => {
        // Tour completed - mark all related tours as done!
        try {
          // Complete all the tours that were part of this flow
          if (!checkTourCompleted(TOUR_KEYS.MAIN)) {
            await authService.completeTour(TOUR_KEYS.MAIN);
          }
          if (!checkTourCompleted(TOUR_KEYS.TRANSACTIONS)) {
            await authService.completeTour(TOUR_KEYS.TRANSACTIONS);
          }
          if (!checkTourCompleted(TOUR_KEYS.CUSTOM_SYMBOLS)) {
            await authService.completeTour(TOUR_KEYS.CUSTOM_SYMBOLS);
          }
          if (!checkTourCompleted(TOUR_KEYS.ANALYSIS)) {
            await authService.completeTour(TOUR_KEYS.ANALYSIS);
          }
          await store.dispatch('auth/fetchUser');
        } catch (error) {
          console.error('Failed to save onboarding state', error);
        }
      };
      currentDriver = createDriver(activeTourOnComplete);

      // Filter steps - steps without elements (like congratulations) are always valid
      const validSteps = analysisContentSteps.filter(step => {
        if (!step.element) return true;
        return document.querySelector(step.element);
      });
      
      if (validSteps.length > 0) {
        activeTourSteps = validSteps;
        currentDriver.setSteps(validSteps);
        currentDriver.drive();
      }
    }, 500);
  };

  // Net Worth page tour - shown when user first visits
  const startNetWorthTour = async () => {
    if (checkTourCompleted(TOUR_KEYS.NET_WORTH)) return;

    destroyCurrentTour();
    await nextTick();

    pendingTimeout = setTimeout(() => {
      pendingTimeout = null;
      activeTourOnComplete = async () => {
        try {
          if (!checkTourCompleted(TOUR_KEYS.NET_WORTH)) {
            await authService.completeTour(TOUR_KEYS.NET_WORTH);
            await store.dispatch('auth/fetchUser');
          }
        } catch (error) {
          console.error('Failed to save net worth tour completion', error);
        }
      };
      currentDriver = createDriver(activeTourOnComplete);

      // Filter steps to only include those with visible elements
      const validSteps = netWorthTourSteps.filter(step => {
        if (!step.element) return true;
        return document.querySelector(step.element);
      });

      if (validSteps.length > 0) {
        activeTourSteps = validSteps;
        currentDriver.setSteps(validSteps);
        currentDriver.drive();
      }
    }, 500);
  };

  // Liabilities page tour - shown when user first visits
  const startLiabilitiesTour = async () => {
    if (checkTourCompleted(TOUR_KEYS.LIABILITIES)) return;

    destroyCurrentTour();
    await nextTick();

    pendingTimeout = setTimeout(() => {
      pendingTimeout = null;
      activeTourOnComplete = async () => {
        try {
          if (!checkTourCompleted(TOUR_KEYS.LIABILITIES)) {
            await authService.completeTour(TOUR_KEYS.LIABILITIES);
            await store.dispatch('auth/fetchUser');
          }
        } catch (error) {
          console.error('Failed to save liabilities tour completion', error);
        }
      };
      currentDriver = createDriver(activeTourOnComplete);

      // Filter steps to only include those with visible elements
      const validSteps = liabilitiesTourSteps.filter(step => {
        if (!step.element) return true;
        return document.querySelector(step.element);
      });

      if (validSteps.length > 0) {
        activeTourSteps = validSteps;
        currentDriver.setSteps(validSteps);
        currentDriver.drive();
      }
    }, 500);
  };

  const onCustomSymbolCreated = async () => {
    // Close any active tour
    destroyCurrentTour();

    // Set step to 6 so TransactionsPage knows to start the use custom symbol tour
    transactionStepRef.value = 6;

    // Navigate back to the portfolio where user was
    if (lastPortfolioId) {
      router.push(`/transactions/${lastPortfolioId}`);
      // The tour will be started by TransactionsPage's onMounted when it sees step 6
    }
  };

  const setLastPortfolioId = (portfolioId) => {
    lastPortfolioId = portfolioId;
  };

  const getLastPortfolioId = () => lastPortfolioId;

  const startIntermediateTour = async (stepType) => {
    if (!checkTourCompleted(TOUR_KEYS.TRANSACTIONS)) {
        destroyCurrentTour();
        await nextTick();

        let steps = [];
        switch(stepType) {
            case 'second': steps = clickAddForSecondStep; break;
            case 'sell': steps = clickAddForSellStep; break;
            case 'customSearch': steps = clickAddForCustomStep; break;
        }

        pendingTimeout = setTimeout(() => {
            pendingTimeout = null;
            activeTourOnComplete = null;
            currentDriver = createDriver(activeTourOnComplete);
            const validSteps = steps.filter(step => document.querySelector(step.element));
            if (validSteps.length > 0) {
                activeTourSteps = validSteps;
                currentDriver.setSteps(validSteps);
                currentDriver.drive();
            }
        }, 500);
    }
  };

  const onTransactionCreated = async () => {
    // Close any active tour
    destroyCurrentTour();

    // Wait for UI to update (holdings watcher will set transactionStep correctly)
    await nextTick();
    
    setTimeout(() => {
      // Use transactionStepRef which was set by holdings watcher with correct state
      // transactionStepRef reflects what the NEXT action should be
      const step = transactionStepRef.value;
      
      if (step === 1) {
        // Just added first txn (AAPL), guide to add Gold next
        startIntermediateTour('second');
      } else if (step === 2) {
        // Just added Gold, guide to sell half next
        startIntermediateTour('sell');
      } else if (step === 4) {
        // Just sold Gold, guide to custom search next
        startIntermediateTour('customSearch');
      } else if (step === 6) {
        // Just added custom symbol transaction (Rolex), show analysis tour
        transactionStepRef.value = 7; // Move to analysis step
        startAnalysisTour();
      }
      // If transactionStepRef >= 7, onboarding is done
    }, 1000);
  };

  const onSymbolNotFound = (portfolioId) => {
    // User searched for a symbol that doesn't exist (like "rolex watch")
    // Save the portfolioId so we can navigate back after creating the symbol
    if (portfolioId) {
      lastPortfolioId = portfolioId;
    }
    
    // Navigate to custom symbols page and start the creation tour
    destroyCurrentTour();
    router.push('/custom-symbols').then(() => {
      setTimeout(() => {
        startCustomSymbolTour();
      }, 500);
    });
  };

  const syncTransactionState = (count, hasGold = false, hasCustomSymbol = false) => {
    transactionCountRef.value = count;

    // Don't downgrade if we're in step 6 or 7 (returning from custom symbol creation or analysis)
    // The user needs to complete the custom symbol transaction and analysis tour
    if (transactionStepRef.value >= 6) {
      return; // Keep current step, don't recalculate
    }
    
    // Logic based on holdings content and count
    if (count === 0) {
        transactionStepRef.value = 0; // Initial -> Start First (AAPL)
    } else if (!hasGold) {
        transactionStepRef.value = 1; // Have txns but no Gold -> Start Second (Add Gold)
    } else if (hasGold && count < 3) {
        transactionStepRef.value = 2; // Have Gold, less than 3 txns -> Start Sell (Sell Gold)
    } else if (!hasCustomSymbol) {
        transactionStepRef.value = 4; // Have Gold, 3+ txns, no custom -> Start Custom Search
    } else {
        transactionStepRef.value = 6; // Have custom symbol -> Use it in transaction
    }
    
    // Cap at Done state
    if (count > 5) transactionStepRef.value = 7; // Done with onboarding
  };

  return {
    startOnboarding,
    startCreationTour,
    startPostCreationTour,
    startPortfolioTour,
    startTransactionModalTour,
    startCustomSymbolTour,
    startCustomSymbolFormTour,
    startUseCustomSymbolTour,
    startCustomSymbolTransactionTour,
    startAnalysisTour,
    startAnalysisContentTour,
    onTransactionCreated,
    onSymbolNotFound,
    onCustomSymbolCreated,
    destroyCurrentTour,
    skipOnboarding,
    syncTransactionState,
    onTransactionSymbolSelected,
    setLastPortfolioId,
    getLastPortfolioId,
    transactionStep: transactionStepRef,
    transactionCount: transactionCountRef,
    // Skip modal controls
    showSkipModal: showSkipModalRef,
    hideSkipModal,
    confirmSkipOnboarding,
    // New tour progress helpers
    checkTourCompleted,
    isMainOnboardingCompleted,
    isPortfolioOnboardingCompleted,
    TOUR_KEYS,
    // Page-specific tours
    startNetWorthTour,
    startLiabilitiesTour
  };
}
